/**
 * The platform's web implementation (KPR-1667; docs/plans/ios-app.md,
 * Decision 2): what keepr does in a browser tab, which is what it did before
 * there was a platform module at all. Nothing here changes behavior.
 *
 * A LEAF. This file imports small utilities and nothing that reaches the
 * network, so the six link builders and the shell can import the platform
 * without dragging the API client in.
 *
 * Every member keeps the shape the app implementation will have (A4,
 * `capacitor.js`), so a caller never asks which one it got:
 *
 *   - secureStore   async get / set / remove; localStorage here, the Keychain there.
 *                   Calls apply in the order they are made (utils/session.js
 *                   writes without awaiting). subscribe(key, cb) reports
 *                   another tab's change. The ONLY reader or writer of the
 *                   session token's storage (KPR-1669;
 *                   src/__tests__/sessionTokenStore.test.js)
 *   - siteOrigin()  the origin of every link a person copies or shares
 *   - saveFile      blob + a[download]; `keepUrlMs` holds the object URL a
 *                   while before it is revoked (a chart export's old way)
 *   - print         window.print(), or a frame's window when given one
 *   - share         copies the link (the app opens the share sheet)
 *   - openExternal  a new tab. An `<a target="_blank">` already opens one
 *                   (components/common/ExternalLink.js), so `nativeLinks` is
 *                   false and the delegated handler (externalLinks.js) steps
 *                   aside; the app sets it true and takes every such click
 *   - openAdmin     the named admin tab (platform/adminWindow.js)
 *   - scan          getUserMedia + the detector (utils/barcode/detector.js).
 *                   `native` is false: the app has a scanner of its own, which
 *                   `capture(formats)` opens
 *   - pickFolder    offered: <input webkitdirectory> (`pick()` opens it)
 *   - network       navigator.onLine and its events
 *   - lifecycle     visibilitychange, as resume / pause
 *   - device        forSignIn(): the `device` a sign-in sends to start a device
 *                   session (KPR-1674; keepr-api SCHEMA.md § Device sessions),
 *                   `{ platform, name }` in the app. null here: a browser
 *                   signs in with the web's 4-week token
 *   - location      where the person is, when they ask: navigator.geolocation
 *   - shell         what only a whole app decides. `landing`: `/` is the public
 *                   landing page for a signed-out visitor (the app has none and
 *                   opens on sign-in). ready() and routeWith() are nothing here
 *   - push, store   no web form yet: available() is false and every call
 *                   rejects with an error whose code is `not_available`
 */
import { openAdminWindow } from './adminWindow';
import { getDetector, scanAvailability } from '../utils/barcode/detector';
import { copyTextToClipboard } from '../utils/clipboard';

const hasWindow = () => typeof window !== 'undefined';

/** The refusal a member with no form on this platform answers with. */
export function notAvailable(member) {
	const error = new Error(`${member} is not available here`);
	error.code = 'not_available';
	error.member = member;
	return error;
}

function storage() {
	try {
		return hasWindow() ? window.localStorage : null;
	} catch {
		// A browser with site data blocked throws on the getter itself.
		return null;
	}
}

const secureStore = Object.freeze({
	async get(key) {
		try {
			const value = storage()?.getItem(key);
			return value === undefined ? null : value;
		} catch {
			return null;
		}
	},
	async set(key, value) {
		try {
			const store = storage();
			if (!store) return false;
			store.setItem(key, String(value));
			return true;
		} catch {
			return false;
		}
	},
	async remove(key) {
		try {
			storage()?.removeItem(key);
		} catch {
			/* nothing stored, nothing to remove */
		}
	},
	// Another tab of this site changed `key` (KPR-1669): the browser's
	// `storage` event, which fires in every OTHER tab sharing localStorage.
	// `callback(value)` gets the new value, or null when it was removed or the
	// other tab cleared storage outright (the event's key is then null). A
	// sessionStorage event is not this store's. The app has one WebView and
	// no other tab, so its store has nothing to report.
	subscribe(key, callback) {
		if (!hasWindow() || !window.addEventListener) return () => {};
		const onStorage = event => {
			if (event.key !== key && event.key !== null) return;
			const local = storage();
			if (event.storageArea && local && event.storageArea !== local) return;
			callback(event.key === null ? null : (event.newValue ?? null));
		};
		window.addEventListener('storage', onStorage);
		return () => window.removeEventListener('storage', onStorage);
	},
});

/**
 * The page's own origin, so a link copied on a preview or a local build names
 * the host it was copied on (the app answers SITE_ORIGIN, config/publicSite.js).
 * `''` with no window: callers fall back to a relative path, as they always did.
 */
function siteOrigin() {
	if (!hasWindow()) return '';
	return window.location?.origin || '';
}

/**
 * The browser's own save flow, from an object URL revoked either way.
 * `keepUrlMs` (chart exports): hold the URL that long before revoking it, as
 * some browsers refuse a URL revoked before the download has started.
 */
async function saveFile(blob, filename, { keepUrlMs = 0 } = {}) { // `anchor` (the pressed control) is read by the app's popover, not here
	const objectUrl = URL.createObjectURL(blob);
	const revoke = () => URL.revokeObjectURL(objectUrl);
	try {
		const anchor = document.createElement('a');
		anchor.href = objectUrl;
		anchor.download = filename || 'download';
		anchor.style.display = 'none';
		document.body.appendChild(anchor);
		try {
			anchor.click();
		} finally {
			anchor.remove();
		}
	} catch (error) {
		revoke();
		throw error;
	}
	if (keepUrlMs > 0) setTimeout(revoke, keepUrlMs);
	else revoke();
	return { saved: true };
}

/**
 * Prints this page, or `target`: the window of a frame the caller built for the
 * purpose (a sign printed from a hidden iframe), which gets focus first.
 */
async function print(target, options) { // options.anchor: see saveFile
	if (target && typeof target.print === 'function') {
		if (typeof target.focus === 'function') target.focus();
		target.print();
		return;
	}
	if (hasWindow() && typeof window.print === 'function') window.print();
}

/** The web has no share sheet keepr uses: sharing a link copies it. Callers own the message. */
async function share({ url } = {}) { // `anchor` too
	const shared = await copyTextToClipboard(url);
	return { shared, method: 'copy' };
}

function openExternal(url) {
	if (!url || !hasWindow()) return null;
	return window.open(url, '_blank', 'noopener,noreferrer');
}

function openAdmin(path, event) {
	return openAdminWindow(path, event);
}

const scan = Object.freeze({
	availability: () => scanAvailability(),
	detector: formats => getDetector(formats),
	native: false,
	capture: () => Promise.reject(notAvailable('scan.capture')),
});

/**
 * Asks for a folder: a hidden `<input webkitdirectory>` that is clicked and
 * taken away again. Resolves `[{ file, path }]` (the path is the file's place
 * under the folder), or `[]` when the person cancels. Call it from a click.
 */
function pickFolderFiles() {
	return new Promise((resolve) => {
		if (typeof document === 'undefined') { resolve([]); return; }
		const input = document.createElement('input');
		input.type = 'file';
		input.multiple = true;
		input.hidden = true;
		input.setAttribute('webkitdirectory', '');
		input.setAttribute('directory', '');
		const done = (files) => {
			input.remove();
			resolve(files);
		};
		input.addEventListener('change', () => done([...(input.files || [])].map(file => ({ file, path: file.webkitRelativePath || file.name }))));
		input.addEventListener('cancel', () => done([]));
		// Attached while it is open: a detached input can be collected before its change fires.
		document.body.appendChild(input);
		input.click();
	});
}

const pickFolder = Object.freeze({
	available: () => true,
	pick: pickFolderFiles,
});

const network = Object.freeze({
	online: () => typeof navigator === 'undefined' || navigator.onLine !== false,
	/** cb(online: boolean) on every change; returns the unsubscribe. */
	subscribe(cb) {
		if (!hasWindow()) return () => {};
		const up = () => cb(true);
		const down = () => cb(false);
		window.addEventListener('online', up);
		window.addEventListener('offline', down);
		return () => {
			window.removeEventListener('online', up);
			window.removeEventListener('offline', down);
		};
	},
});

const lifecycle = Object.freeze({
	/** cb('resume' | 'pause') as the tab is shown or hidden; returns the unsubscribe. */
	subscribe(cb) {
		if (typeof document === 'undefined') return () => {};
		const onChange = () => cb(document.visibilityState === 'hidden' ? 'pause' : 'resume');
		document.addEventListener('visibilitychange', onChange);
		return () => document.removeEventListener('visibilitychange', onChange);
	},
});

// A browser is not a device session (KPR-1674): its sign-in sends no
// `device`, so the API answers the web's token, with no refresh credential.
const device = Object.freeze({
	forSignIn: () => null,
});

/** Where the person is, for a field that asks. Rejects with the browser's own error. */
const location = Object.freeze({
	available: () => typeof navigator !== 'undefined' && Boolean(navigator.geolocation),
	current: options => new Promise((resolve, reject) => {
		navigator.geolocation.getCurrentPosition(resolve, reject, options);
	}),
});

const shell = Object.freeze({
	landing: true,
	ready() {},
	routeWith() {},
});

const refuse = member => () => Promise.reject(notAvailable(member));

const push = Object.freeze({
	available: () => false,
	permission: refuse('push'),
	register: refuse('push'),
	unregister: refuse('push'),
	subscribe: refuse('push'),
});

const store = Object.freeze({
	available: () => false,
	open: refuse('store'),
});

const web = Object.freeze({
	kind: 'web',
	nativeLinks: false,
	secureStore,
	siteOrigin,
	saveFile,
	print,
	share,
	openExternal,
	openAdmin,
	scan,
	pickFolder,
	push,
	network,
	lifecycle,
	store,
	device,
	location,
	shell,
});

export default web;
